import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import API from '@Services/apiAxios';

// Define your payload type
interface PayloadMarkup {
  csp: string;
  system: string;
}

interface DownloadRateSheetArgs {
  rate: PayloadMarkup;
  RateState: any; // adjust type properly
  RateConfig: any; // adjust type properly
  navigatedDate: string;
}

// Thunk
export const downloadUserRateSheet = createAsyncThunk<
  string, // Return type (result.data)
  DownloadRateSheetArgs, // Thunk argument
  { rejectValue: string }
>('rateSheet/downloadRateSheet', async ({ rate, RateState, RateConfig, navigatedDate }, { rejectWithValue }) => {
  const sheetData = RateState.sheets[rate?.csp + rate?.system];
  const rateParams = {
    accountNumber: sheetData.accountNumber,
    documentRates: sheetData.documentRates,
    startDate: sheetData.startDate,
    endDate: sheetData.endDate,
    csp: sheetData.csp,
    system: sheetData.system,
    isImport: RateConfig.isImport,
    navigatedDate,
  };
  const accountNumber = sheetData.accountNumber || 'default';
  const searchParams = new URLSearchParams();
  searchParams.append('sheet', encodeURIComponent(JSON.stringify(rateParams)));
  const searchParamsStr = '?' + searchParams.toString();

  try {
    const result = await API.get('/shipment/download/rate/' + accountNumber + searchParamsStr);
    return result.data;
  } catch (error: any) {
    const errorRes = error?.response?.data;
    return rejectWithValue(errorRes?.msg || 'Something went wrong');
  }
});

interface RateSheetState {
  downloadStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  downloadUrl: string;
  error: string | null;
}

const initialState: RateSheetState = {
  downloadStatus: 'idle',
  downloadUrl: '',
  error: null,
};

const rateSheetSlice = createSlice({
  name: 'UserRateSheet',
  initialState,
  reducers: {
    resetUserDownloadState: state => {
      state.downloadStatus = 'idle';
      state.downloadUrl = '';
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(downloadUserRateSheet.pending, state => {
        state.downloadStatus = 'loading';
        state.downloadUrl = '';
        state.error = null;
      })
      .addCase(downloadUserRateSheet.fulfilled, (state, action) => {
        state.downloadStatus = 'succeeded';
        state.downloadUrl = action.payload;
      })
      .addCase(downloadUserRateSheet.rejected, (state, action) => {
        state.downloadStatus = 'failed';
        state.error = action.payload || 'Download failed';
      });
  },
});

export const { resetUserDownloadState } = rateSheetSlice.actions;
export default rateSheetSlice.reducer;
